import { useEffect, useState } from 'react'
import { Accordion, Card, Col, Row, useAccordionToggle } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import {
  getLearningUserDetail,
  resendInvite,
} from '../../../store/knowledge/actions'
import { getCommonState } from '../../../store/common/selector'
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal'
import ForgotPasswordModal from '../../../routes/Users/partials/ForgotPasswordModal'
import { Button, Skeleton, Box, Typography } from '@mui/material'

import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

function AdminLearnerTile({
  user,
  editAdminToggleHandler,
  deleteAdminHandler,
  classMode,
  isAdmin,
  isLearner,
  addToSelectedRows,
  selectedNodes,
  RemoveButtonTitle,
}: any) {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const commonState = useSelector(getCommonState)
  const [userDetails, setUserDetails] = useState<any>(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showsResendInviteModal, setShowsResendInviteModal] = useState(false)
  const [showsForgotPasswordModal, setShowsForgotPasswordModal] =
    useState(false)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false)
  const isSuperadmin = commonState && commonState?.user?.role === 'superadmin'

  useEffect(() => {
    setUserDetails(user)
  }, [user])

  const ContextAwareToggle = ({
    children,
    eventKey,
    callback,
    is_suggested,
    _id,
  }: any) => {
    const decoratedOnClick = useAccordionToggle(eventKey, async () => {
      if (userDetails && !userDetails.userClasses) {
        setIsLoadingDetails(true)
        try {
          const response: any = await dispatch(getLearningUserDetail(eventKey))
          setUserDetails({
            ...userDetails,
            userClasses: response,
          })
        } finally {
          setIsLoadingDetails(false)
        }
      }
    })
    return (
      <>
        {addToSelectedRows && (
          <input
            type="checkbox"
            className="mr-2"
            onChange={e => {
              addToSelectedRows(e, _id)
            }}
            checked={selectedNodes.indexOf(eventKey) >= 0}
          />
        )}
        <span
          className={`${is_suggested ? 'is_suggested' : ''} ${
            addToSelectedRows ? '' : 'pl-3'
          } py-2`}
          onClick={decoratedOnClick}
        >
          {children}
        </span>
      </>
    )
  }

  const resendInviteHandler = async (user: any) => {
    try {
      setSelectedUser(user)
      setShowsResendInviteModal(true)
    } catch (error: any) {}
  }

  const forgotPasswordHandler = async (user: any) => {
    try {
      setSelectedUser(user)
      setShowsForgotPasswordModal(true)
    } catch (error: any) {}
  }

  const confirmResendInviteHandler = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.preventDefault()
    setShowsResendInviteModal(false)
    if (selectedUser) {
      await dispatch(resendInvite(selectedUser._id))
    }
  }

  if (!userDetails) {
    return <></>
  }

  return (
    <>
      <ConfirmationModal
        message={t('Are you sure want to resend the invite?')}
        size={'md'}
        show={showsResendInviteModal}
        selectedRow={selectedUser}
        onHide={() => setShowsResendInviteModal(false)}
        confirmHandler={confirmResendInviteHandler}
        buttonText={t('Resend Invite')}
      />
      {isSuperadmin && (
        <ForgotPasswordModal
          user_id={selectedUser?._id}
          show={showsForgotPasswordModal}
          size={'lg'}
          onHide={() => setShowsForgotPasswordModal(false)}
        />
      )}

      <Card>
        <Card.Header>
          <ContextAwareToggle
            // is_suggested={knowledge.is_suggested}
            _id={userDetails._id}
            eventKey={userDetails._id}
          >
            <Row>
              <Col className="col-lg-3 col-6">
                <small className="font-sm">{t('First Name')}</small>
                <div>{userDetails.first_name}</div>
              </Col>
              <Col className="col-lg-2 col-6">
                <small className="font-sm">{t('Last Name')}</small>
                <div>{userDetails.last_name}</div>
              </Col>
              <Col className="col-lg-5 col-6">
                <small className="font-sm">{t('Email')}</small>
                <div>{userDetails.email}</div>
              </Col>
              <Col className="col-lg-2 col-6">
                <small className="font-sm">{t('Status')}</small>
                {userDetails.status === 'active' && (
                  <div className="clr-green">{t('Active')}</div>
                )}
                {userDetails.status === 'inactive' && (
                  <div className="clr-orange">{t('Pending Invite')}</div>
                )}
              </Col>
            </Row>
          </ContextAwareToggle>
        </Card.Header>
        <Accordion.Collapse eventKey={userDetails._id}>
          <Card.Body>
            {isLoadingDetails ? (
              <Box sx={{ py: 3, px: 2 }}>
                <Row>
                  <Col lg={3} sm={6} className="mb-3">
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="80%" height={24} />
                  </Col>
                  <Col lg={3} sm={6} className="mb-3">
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="80%" height={24} />
                  </Col>
                  <Col lg={3} sm={6} className="mb-3">
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="80%" height={24} />
                  </Col>
                  <Col lg={3} sm={6} className="mb-3">
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="text" width="80%" height={24} />
                  </Col>
                  <Col md={6} className="mb-3">
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Skeleton variant="rounded" width={80} height={28} />
                      <Skeleton variant="rounded" width={100} height={28} />
                      <Skeleton variant="rounded" width={90} height={28} />
                    </Box>
                  </Col>
                  <Col md={6} className="mb-3">
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Skeleton variant="rounded" width={95} height={28} />
                      <Skeleton variant="rounded" width={110} height={28} />
                    </Box>
                  </Col>
                </Row>
              </Box>
            ) : (
              <Row>
                {!isLearner && (
                  <>
                    {!isAdmin && (
                      <>
                        <Col lg={3} sm={6} className="mb-3">
                          <h3 className="clr-black mb-1">{t('First Name')}</h3>
                          <Typography>{userDetails.first_name}</Typography>
                        </Col>
                        <Col lg={3} sm={6} className="mb-3">
                          <h3 className="clr-black mb-1">{'Last Name'}</h3>
                          <Typography>{userDetails.last_name}</Typography>
                        </Col>
                      </>
                    )}
                    <Col md={6} className="mb-3">
                      <h3 className="clr-black mb-1">{t('Permission')}</h3>
                      <Typography>
                        {userDetails.role === 'readonlyadmin'
                          ? t('View Only')
                          : userDetails.role === 'admin' ||
                              userDetails.is_superadmin
                            ? t('Can Edit')
                            : ''}
                      </Typography>
                    </Col>
                    {userDetails.userClasses &&
                      userDetails.userClasses.last_active_at && (
                        <Col md={6} className="mb-3">
                          <h3 className="clr-black mb-1">
                            {t('Last Activity')}
                          </h3>
                          <Typography>
                            {dayjs
                              .utc(userDetails.userClasses.last_active_at)
                              .local()
                              .format('DD/MM/YYYY')}
                          </Typography>
                        </Col>
                      )}
                  </>
                )}

                {!isAdmin && (
                  <>
                    <Col md={6} className="mb-3">
                      <h3 className="clr-black mb-1">{t('Classes')}</h3>
                      <div className="key-tags d-flex flex-wrap align-items-center">
                        {userDetails.userClasses &&
                          userDetails.userClasses.classes &&
                          userDetails.userClasses.classes.map(
                            (classes: any) => {
                              return (
                                <div className="bg-none">
                                  <span>{classes?.name}</span>
                                </div>
                              )
                            },
                          )}
                      </div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <h3 className="clr-black mb-1">{t('Learner Groups')}</h3>
                      <div className="key-tags d-flex flex-wrap align-items-center">
                        {userDetails.userClasses &&
                          userDetails.userClasses.groups &&
                          userDetails.userClasses.groups.map((classes: any) => {
                            return (
                              <div className="bg-none">
                                <span>{classes?.name}</span>
                              </div>
                            )
                          })}
                      </div>
                    </Col>
                  </>
                )}

                {isLearner &&
                  userDetails.userClasses &&
                  userDetails.userClasses?.last_active_at && (
                    <Col md={6} className="mb-3">
                      <h3 className="clr-black mb-1">{t('Last Activity')}</h3>
                      <Typography>
                        {dayjs
                          .utc(userDetails.userClasses.last_active_at)
                          .local()
                          .format('DD/MM/YYYY')}
                      </Typography>
                    </Col>
                  )}
              </Row>
            )}

            <div className="mt-3 text-right">
              {!classMode && (
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ mx: 1, textTransform: 'none' }}
                  onClick={() => editAdminToggleHandler(userDetails, true)}
                >
                  {t('Edit')}
                </Button>
              )}
              {userDetails.status === 'active' && isSuperadmin && (
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ mx: 1, textTransform: 'none' }}
                  onClick={() => forgotPasswordHandler(userDetails)}
                >
                  {t('Forgot Password')}
                </Button>
              )}
              {userDetails.status === 'inactive' && (
                <Button
                  variant="outlined"
                  color="primary"
                  sx={{ mx: 1, textTransform: 'none' }}
                  onClick={() => resendInviteHandler(userDetails)}
                >
                  {t('Resend Invite')}
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                sx={{ mx: 1, textTransform: 'none' }}
                onClick={() =>
                  deleteAdminHandler(userDetails._id, userDetails.role)
                }
              >
                {RemoveButtonTitle}
              </Button>
            </div>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </>
  )
}

export default AdminLearnerTile
